import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { comissaoService } from '@/services/comissaoService'
import { RecorrenciaReserva } from '@/types/comissao'

/**
 * Marco da recorrência da comissão (AN-PP5 §3.5): ATIVACAO ("Registrar ativação") ou
 * ENCERRAMENTO ("Encerrar recorrência"). Só back-office — quem abre decide pelo papel.
 *
 * Nada é decidido aqui: o evento de ativação e a frase de confirmação vêm do backend
 * (`GET …/recorrencia`); a validação (evento da política, reserva ATIVO/ENCERRADA, data ≥
 * assinatura, ativação já registrada) é do servidor, e a mensagem 409/422 dele é exibida como
 * vem.
 */

export type TipoMarcoDialog = 'ATIVACAO' | 'ENCERRAMENTO'

interface Props {
  open: boolean
  tipo: TipoMarcoDialog
  oportunidadeId: string
  recorrencia: RecorrenciaReserva | null
  onClose: () => void
  onRegistrado: () => void
}

/** Mensagem 409/422 do backend exatamente como vem (o interceptor põe `body.message` em `message`). */
const mensagemDoBackend = (e: unknown, fallback: string): string => {
  const msg = (e as { message?: unknown } | null | undefined)?.message
  return typeof msg === 'string' && msg ? msg : fallback
}

const normalizar = (s: string) => s.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR')

const RegistrarAtivacaoDialog: React.FC<Props> = ({
  open,
  tipo,
  oportunidadeId,
  recorrencia,
  onClose,
  onRegistrado,
}) => {
  const hoje = dayjs().format('YYYY-MM-DD')
  const [dataMarco, setDataMarco] = useState(hoje)
  const [confirmacao, setConfirmacao] = useState('')
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setDataMarco(dayjs().format('YYYY-MM-DD'))
    setConfirmacao('')
    setMotivo('')
    setErro(null)
  }, [open])

  const ativacao = tipo === 'ATIVACAO'
  const frase = recorrencia?.confirmacaoAtivacao ?? null
  const evento = recorrencia?.politica?.eventoAtivacao ?? null

  const podeSalvar = ativacao
    ? Boolean(frase && evento && dataMarco && normalizar(confirmacao) === normalizar(frase))
    : Boolean(dataMarco && motivo.trim())

  const salvar = async () => {
    setSalvando(true)
    setErro(null)
    try {
      await comissaoService.registrarMarco(
        oportunidadeId,
        ativacao
          ? { tipo: 'ATIVACAO', eventoAtivacao: evento, dataMarco, confirmacao: frase }
          : { tipo: 'ENCERRAMENTO', dataMarco, motivo: motivo.trim() }
      )
      onRegistrado()
    } catch (e) {
      setErro(
        mensagemDoBackend(
          e,
          ativacao ? 'Falha ao registrar a ativação.' : 'Falha ao encerrar a recorrência.'
        )
      )
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{ativacao ? 'Registrar ativação' : 'Encerrar recorrência'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          {ativacao && !frase && (
            <Alert severity="warning">
              Esta reserva não tem ativação a registrar segundo a política do produto.
            </Alert>
          )}
          {ativacao && recorrencia?.rotuloEventoAtivacao && (
            <Typography variant="body2">
              Evento da política: <strong>{recorrencia.rotuloEventoAtivacao}</strong>
              {recorrencia.politica?.versao ? ` (política ${recorrencia.politica.versao})` : ''}
            </Typography>
          )}
          <TextField
            label={ativacao ? 'Data da ativação' : 'Data do encerramento'}
            type="date"
            value={dataMarco}
            onChange={(e) => setDataMarco(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: hoje }}
            helperText="Data real do evento, não a de hoje."
            fullWidth
          />
          {ativacao && frase && (
            <TextField
              label="Confirmação"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              helperText={`Digite "${frase}" para confirmar.`}
              fullWidth
            />
          )}
          {!ativacao && (
            <TextField
              label="Motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              helperText='Ex.: "Originei deixou de receber".'
              multiline
              rows={2}
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={salvando || !podeSalvar}
        >
          {salvando ? 'Salvando…' : ativacao ? 'Registrar' : 'Encerrar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RegistrarAtivacaoDialog
